import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import {
  api,
  errorMessage,
  type AdminUser,
  type Channel,
  type Instance,
  type Job,
  type Me,
  type PlanStep,
} from '../api';
import { relativeTime } from '../format';
import { useInterval } from '../hooks';
import { Link } from '../router';
import { Alert } from './Alert';
import { ConfirmDialog } from './ConfirmDialog';
import { StatusBadge } from './StatusBadge';

interface Props {
  me: Me;
  onJob: (job: Job) => void;
  notice: { text: string; jobId: string } | null;
  onShowJobs: (jobId: string) => void;
}

function InstanceRow({
  user,
  instance,
  disabled,
  busy,
  onChannel,
  onRetry,
  onRemove,
}: {
  user: AdminUser;
  instance: Instance;
  disabled: boolean;
  busy: boolean;
  onChannel: (channel: Channel) => void;
  onRetry: () => void;
  onRemove: () => void;
}) {
  const id = useId();
  const [channel, setChannel] = useState<Channel>(instance.channel);
  useEffect(() => setChannel(instance.channel), [instance.channel]);
  const cloud = instance.kind === 'cloud';
  const active = instance.status !== 'removed';

  return (
    <li className="inst">
      <div className="inst-main">
        <strong>{cloud ? 'Cloud' : 'Local'}</strong>
        <StatusBadge status={instance.status} />
        <span className="muted">
          {instance.channel} · {instance.version ?? 'version inconnue'} · {relativeTime(instance.lastSeenAt)}
        </span>
        {instance.url && (
          <a href={instance.url} target="_blank" rel="noreferrer noopener" className="url">
            {instance.url}
          </a>
        )}
        {!cloud && instance.localPort && <span className="muted">port {instance.localPort}</span>}
      </div>
      {active && (
        <div className="inst-actions">
          <Link className="btn btn-small" href={`/admin/instances/${encodeURIComponent(instance.id)}`}>
            Configurer{' '}
            <span className="sr-only">
              l'instance {cloud ? 'cloud' : 'locale'} de {user.username}
            </span>
          </Link>
          {cloud && (
            <>
              <label htmlFor={id} className="sr-only">
                Canal de l'instance de {user.username}
              </label>
              <select
                id={id}
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
                disabled={busy}
              >
                <option value="stable">stable</option>
                <option value="canary">canary</option>
              </select>
              <button
                type="button"
                className="btn btn-small"
                disabled={busy || channel === instance.channel}
                onClick={() => onChannel(channel)}
              >
                Changer le canal
              </button>
              {instance.status === 'failed' && (
                <button type="button" className="btn btn-small" disabled={busy} onClick={onRetry}>
                  Relancer
                </button>
              )}
              <button
                type="button"
                className="btn btn-small btn-danger-outline"
                disabled={busy || disabled}
                onClick={onRemove}
              >
                Retirer <span className="sr-only">l'instance de {user.username}</span>
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function UsersTab({ me, onJob, notice, onShowJobs }: Props) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const disabled = me.provisioningMode === 'disabled';

  const load = useCallback(async () => {
    try {
      setUsers(await api.adminUsers());
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const provisioning = (users ?? []).some((u) => u.instances.some((i) => i.status === 'provisioning'));
  useInterval(() => void load(), provisioning ? 5000 : null);

  // Création
  const formId = useId();
  const [username, setUsername] = useState('');
  const [channel, setChannel] = useState<Channel>('stable');
  const [plan, setPlan] = useState<{ username: string; channel: Channel; steps: PlanStep[] } | null>(null);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const trimmed = username.trim();

  async function preview(e: FormEvent) {
    e.preventDefault();
    if (!trimmed) return;
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.createInstance(trimmed, channel, true);
      if ('plan' in r) setPlan({ username: trimmed, channel, steps: r.plan });
    } catch (err) {
      setPlan(null);
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.createInstance(trimmed, channel);
      if ('job' in r) onJob(r.job);
      setConfirmCreate(false);
      setPlan(null);
      setUsername('');
      await load();
    } catch (err) {
      setConfirmCreate(false);
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Actions par instance
  const [actionError, setActionError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ user: AdminUser; instance: Instance } | null>(null);
  const [typed, setTyped] = useState('');

  async function act(fn: () => Promise<{ job: Job }>) {
    setBusy(true);
    setActionError(null);
    try {
      const { job } = await fn();
      onJob(job);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
      setRemoving(null);
      setTyped('');
    }
  }

  return (
    <div className="stack">
      {disabled && (
        <Alert kind="warning">
          Le provisionnement est désactivé sur ce serveur : la création et le retrait d'instances sont
          indisponibles.
        </Alert>
      )}
      {notice && (
        <Alert kind="success">
          {notice.text}{' '}
          <button type="button" className="link-button" onClick={() => onShowJobs(notice.jobId)}>
            Voir la tâche
          </button>
        </Alert>
      )}

      <section className="panel" aria-labelledby={`${formId}-t`}>
        <h2 id={`${formId}-t`}>Créer une instance cloud</h2>
        <form className="inline-form" onSubmit={preview}>
          <div className="field">
            <label htmlFor={`${formId}-u`}>Nom d'utilisateur</label>
            <input
              id={`${formId}-u`}
              type="text"
              list={`${formId}-users`}
              value={username}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => {
                setUsername(e.target.value);
                setPlan(null);
              }}
            />
            <datalist id={`${formId}-users`}>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.username} />
              ))}
            </datalist>
          </div>
          <div className="field">
            <label htmlFor={`${formId}-c`}>Canal</label>
            <select
              id={`${formId}-c`}
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value as Channel);
                setPlan(null);
              }}
            >
              <option value="stable">stable</option>
              <option value="canary">canary</option>
            </select>
          </div>
          <button type="submit" className="btn" disabled={busy || disabled || !trimmed}>
            Prévisualiser
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || disabled || !trimmed}
            onClick={() => setConfirmCreate(true)}
          >
            Créer
          </button>
        </form>
        {formError && <Alert>{formError}</Alert>}
        {plan && (
          <div className="plan" role="region" aria-label="Plan de création">
            <h3>
              Plan prévu pour {plan.username} ({plan.channel})
            </h3>
            <ol>
              {plan.steps.map((s) => (
                <li key={s.step}>
                  <strong>{s.step}</strong> — {s.description}
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      {actionError && <Alert>{actionError}</Alert>}
      {error && <Alert>{error}</Alert>}
      {users === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des utilisateurs…
        </p>
      )}
      {users?.length === 0 && <p className="muted">Aucun utilisateur connu.</p>}
      {users && users.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Utilisateurs et instances</caption>
            <thead>
              <tr>
                <th scope="col">Utilisateur</th>
                <th scope="col">Dernière connexion</th>
                <th scope="col">Instances</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <th scope="row" className="user-cell">
                    <div>{u.displayName || u.username}</div>
                    <div className="muted">{u.username}</div>
                    <div className="muted">{u.email}</div>
                  </th>
                  <td className="nowrap">{relativeTime(u.lastLoginAt)}</td>
                  <td>
                    {u.instances.length === 0 ? (
                      <span className="muted">Aucune instance</span>
                    ) : (
                      <ul className="inst-list">
                        {u.instances.map((i) => (
                          <InstanceRow
                            key={i.id}
                            user={u}
                            instance={i}
                            disabled={disabled}
                            busy={busy}
                            onChannel={(c) => void act(() => api.changeChannel(i.id, c))}
                            onRetry={() => void act(() => api.retryInstance(i.id))}
                            onRemove={() => {
                              setTyped('');
                              setRemoving({ user: u, instance: i });
                            }}
                          />
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmCreate && (
        <ConfirmDialog
          title="Confirmer la création"
          confirmLabel="Créer l'instance"
          busy={busy}
          onConfirm={() => void create()}
          onCancel={() => setConfirmCreate(false)}
        >
          <p>
            Créer une instance cloud <strong>{channel}</strong> pour <strong>{trimmed}</strong> ?
          </p>
          <p className="muted">
            URL prévue : https://{trimmed}.{me.baseDomain}/
          </p>
        </ConfirmDialog>
      )}

      {removing && (
        <ConfirmDialog
          title="Retirer l'instance"
          confirmLabel="Retirer l'instance"
          danger
          busy={busy}
          confirmDisabled={typed !== removing.user.username}
          onConfirm={() => void act(() => api.removeInstance(removing.instance.id))}
          onCancel={() => setRemoving(null)}
        >
          <p>
            L'instance de <strong>{removing.user.username}</strong> sera retirée. Le namespace et les données
            sont conservés : la purge est manuelle.
          </p>
          <div className="field">
            <label htmlFor={`${formId}-rm`}>
              Retapez <code>{removing.user.username}</code> pour confirmer
            </label>
            <input
              id={`${formId}-rm`}
              type="text"
              value={typed}
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
